import React, { FC, useCallback, useMemo } from "react";
import { IconToggle, IOption, Menu, Popup } from "@evergis/ui";
import { useTranslation } from "react-i18next";
import { IconTypesKeys } from "@evergis/ui/dist/atoms/Icon";

import { useToggle } from "../../../../hooks/useToggle";
import { useAccessDialog } from "../../hooks/useAccessDialog";
import { useLimitsExceededCheck } from "../../../../hooks/useLimitsExceededCheck";
import { useCopyResourceDialog, useExportDialog } from "../../../../hooks/useDialogs";

enum CardControlsMenuOptions {
  Access = "access",
  Copy = "copy",
  Export = "export",
}

interface CardControlsMenuProps {
  resourceName: string;
  resourceAlias?: string;
  resourceType: string;
  isAccessVisible?: boolean;
  isCopyVisible?: boolean;
  isExportVisible?: boolean;
}

export const CardControlsMenu: FC<CardControlsMenuProps> = ({
  resourceName,
  resourceAlias,
  resourceType,
  isAccessVisible,
  isCopyVisible,
  isExportVisible,
}) => {
  const { t } = useTranslation("common");
  const [isVisiblePopup, togglePopup] = useToggle(false);
  const processCopyDialog = useLimitsExceededCheck(useCopyResourceDialog());
  const processExportDialog = useExportDialog();

  const openAccessDialog = useAccessDialog();

  const openCopyDialog = useCallback(
    () => processCopyDialog([resourceName, resourceType]),
    [processCopyDialog, resourceName, resourceType],
  );

  const openExportDialog = useCallback(
    () => processExportDialog([resourceName, resourceAlias, resourceType]),
    [processExportDialog, resourceName, resourceAlias, resourceType],
  );

  const menuOptions = useMemo(
    () =>
      [
        isAccessVisible
          ? { text: t("accessSettings"), value: CardControlsMenuOptions.Access, icon: "key" as IconTypesKeys }
          : undefined,
        isCopyVisible
          ? { text: t("actions.copy"), value: CardControlsMenuOptions.Copy, icon: "copy" as IconTypesKeys }
          : undefined,
        isExportVisible
          ? { text: t("actions.export"), value: CardControlsMenuOptions.Export, icon: "download" as IconTypesKeys }
          : undefined,
      ].filter(Boolean),
    [isAccessVisible, isCopyVisible, isExportVisible, t],
  );

  const onOptionSelect = useCallback(
    (items: IOption[]) => {
      const selectedOption = items[0];

      switch (selectedOption.value) {
        case CardControlsMenuOptions.Access:
          openAccessDialog(resourceName, resourceType);
          break;
        case CardControlsMenuOptions.Copy:
          openCopyDialog();
          break;
        case CardControlsMenuOptions.Export:
          openExportDialog();
          break;
        default:
          break;
      }

      isVisiblePopup && togglePopup();
    },
    [isVisiblePopup, openAccessDialog, openCopyDialog, openExportDialog, togglePopup],
  );

  if (!isAccessVisible && !isCopyVisible && !isExportVisible) {
    return null;
  }

  return (
    <Popup
      isVisible={isVisiblePopup}
      onRequestClose={togglePopup}
      anchor={ref => <IconToggle kind={"menu_vertical"} innerRef={ref} onClick={togglePopup} />}
    >
      <Menu options={menuOptions} selectOption={onOptionSelect} />
    </Popup>
  );
};
